import { makeAutoObservable, runInAction } from "mobx";
import {IChangeable, ISaveable, WithOld} from "../../../stores/types";
import {
    IPolicyRule,
    CONDITION_GROUP_LEVELS,
    IReturnMethodPreset,
    FEE_TYPE,
    IResolutionPreset,
    IPolicyFee,
    IPolicyConditionGroup,
    SHIPPING_LABEL_FEE_TYPES,
    CONDITION_GROUP_OPERATOR,
    DISPLAY_FEE_OPTION,
    IPolicyRuleAvailability,
} from "../policy-types";
import PolicyConditionGroupStore from "./policy-condition-group-store";
import PolicyFeeStore from "./policy-fee-store";
import { PolicyAddressRequirementsStore } from "./policy-address-requirements-store";
import {
    APPLY_ON,
    APPROVAL_PROCESS,
    AUTO_MARK_DONE,
    AUTO_MARK_DONE_TRIGGER,
    AUTO_MARK_DONE_TRIGGER_OPTIONS_TREE,
    AUTO_PROCESS_EXECUTE,
    CHARGE_FEES,
    DEFAULT_AUTO_MARK_DONE_SINCE_VALUE,
    DISPLAY_FEE_OPTION_MAP,
    FEATURES_RESOLUTIONS_IDS_MAP,
    FEE_STORE_LEVEL,
    POLICY_FEES_DEFAULS_BY_FEE_STORE_LEVEL,
    RESOLUTION_TYPES_SUPPORTING_GIFT_RETURNS_ELIGIBILITY,
    SINCE_UNIT,
    USED_FOR_OPTIONS,
    approvalProcessOptionsList,
} from "../constants";
import {
    INSTANT_REFUND_PROVIDER,
    REFUND_RESOLUTIONS_TYPES,
    RELEASE_ABLE_RESOLUTIONS_IDS,
    RESOLUTION_ID,
    RESOLUTION_IDS_OF_TYPE_EXCHANGE,
    RESOLUTION_TYPE,
} from "../../resolutions/constants";
import ConditionsPreset from "./presets/conditions-preset";
import ReturnMethodsPreset from "./presets/return-methods-preset";
import ResolutionsPreset from "./presets/resolutions-preset";
import {
    AUTO_PROCESS_TRIGGER,
    AUTO_PROCESS_VALIDATED_QUALIFIER,
    RETURN_LABEL_FEE_PAYMENT,
} from "../../../models/User";
import {
    RETURN_METHOD_TYPE,
    TRACKING_TYPE,
} from "../../return-methods/return-method-types";
import AggregatorsInfoStore from "../../return-methods/stores/aggregators-info-store";
import ReturnReasonsPreset from "./presets/return-reasons-preset";
import currentPortalStore from "../../../stores/current-portal-store";
import { PolicyListStore } from "./policy-list-store";
import PolicyService from "../../../services/api/policy-service";
import { TYPE, rootContainer } from "../../../services/container/root";
import { FEATURES } from "../../../models/Plan";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { ICheckboxesTreeItem } from "../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";
import IconReturnGoScoreAlgo from "../../../images/return-score.svg";
import IconProviderRefundid from "../../../images/refundid.svg";
import IconProviderReveni from "../../../images/reveni.svg";
import IconProviderRise from "../../../images/rise.svg";
import SideEffectDetector from "../../../stores/side-effect-detector";
import IframeService from "../../../services/iframe-service/iframe-service";
import rmsMixpanelStore from "../../../stores/rms-mixpanel-store";
import {
    POLICY_RULES_MIXPANEL_SAVE_NEW_RULE,
    getPolicyRulesMixpanelSaveEvent,
} from "../metrics/policy-rules-mixpanel-events";
import {
    toBoolean,
    toNullableNumber,
} from "../../../models/model-types-methods";
import { SHIPPING_CONNECTION_CODE } from "../../../models/IntegrationsTypes";

interface IPolicyConditionGroupByLevelMap {
    [key: string]: PolicyConditionGroupStore[];
}
interface ISideEffect {
    confirmSideEffect?: () => void;
    settingName?: string;
    settingValue?: string;
}
interface IFeesMap {
    [key: string]: PolicyFeeStore;
}

export interface IPolicyRuleChanges
    extends Partial<Omit<IPolicyRule, "return_method_id">> {
    _client_id?: number | string;
    return_method_id?: number | null;
}
class PolicyStore implements IChangeable<IPolicyRule>, ISaveable {
    private _model: IPolicyRule = {} as IPolicyRule;
    private _conditionPreset: ConditionsPreset = {} as ConditionsPreset;
    private _returnMethodsPreset: ReturnMethodsPreset =
        {} as ReturnMethodsPreset;
    private _returnReasonsPreset: ReturnReasonsPreset =
        {} as ReturnReasonsPreset;
    private _resolutionsPreset: ResolutionsPreset = {} as ResolutionsPreset;
    private _aggregatorInfoPreset: AggregatorsInfoStore =
        {} as AggregatorsInfoStore;
    private _addressRequirementsStore: PolicyAddressRequirementsStore;
    private _changes: IPolicyRuleChanges = {};
    private _fees: IFeesMap = {};
    private _conditionGroupBylevel: IPolicyConditionGroupByLevelMap = {};
    private _isSaving: boolean = false;
    private _showDisplayAsDisabledConfirm: boolean = false;
    private _isDeleting: boolean = false;
    private _sideEffect: ISideEffect = {};
    private _shouldRefresh: boolean = false;

    constructor(private _policyListStore: PolicyListStore, model: IPolicyRule) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._conditionPreset = new ConditionsPreset(
            _policyListStore.settings.conditions
        );
        this._returnMethodsPreset = new ReturnMethodsPreset(
            _policyListStore.settings.return_methods
        );
        this._returnReasonsPreset = new ReturnReasonsPreset(
            _policyListStore.settings.return_reasons
        );
        this._resolutionsPreset = new ResolutionsPreset(
            _policyListStore.settings.resolutions
        );
        this._aggregatorInfoPreset = new AggregatorsInfoStore(
            _policyListStore.settings.aggregators_info
        );
        this._addressRequirementsStore = new PolicyAddressRequirementsStore();
        this._setModel(model);
    }

    private get _iframeService(): IframeService {
        return rootContainer.get<IframeService>(TYPE.IframService);
    }

    private _setModel(model: IPolicyRule): void {
        this._model = ensureValidModelCopy(model);
        this._changes = {} as IPolicyRuleChanges;

        this._model.policy_rule_availability = model.policy_rule_availability;

        this._addressRequirementsStore.setModel(model.address_requirements);

        /** generate all sub stores here to update their model and reset changes */
        const restocking_fee = model.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.RESTOCKING_FEE
        );
        const cancellation_fee = model.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.CANCELLATION_FEE
        );
        const return_fee = model.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.RETURN_FEE
        );
        const shipping_label_fee = model.policy_rule_fees?.find(
            (policy_fee) => {
                return (
                    policy_fee.fee_type === null ||
                    SHIPPING_LABEL_FEE_TYPES.indexOf(
                        policy_fee.fee_type as FEE_TYPE
                    ) >= 0
                );
            }
        );
        this._fees = {
            [FEE_STORE_LEVEL.RESTOCK]: new PolicyFeeStore(
                restocking_fee,
                POLICY_FEES_DEFAULS_BY_FEE_STORE_LEVEL[FEE_STORE_LEVEL.RESTOCK],
                this
            ),
            [FEE_STORE_LEVEL.RETURN]: new PolicyFeeStore(
                return_fee,
                POLICY_FEES_DEFAULS_BY_FEE_STORE_LEVEL[FEE_STORE_LEVEL.RETURN],
                this
            ),
            [FEE_STORE_LEVEL.CANCEL]: new PolicyFeeStore(
                cancellation_fee,
                POLICY_FEES_DEFAULS_BY_FEE_STORE_LEVEL[FEE_STORE_LEVEL.CANCEL],
                this
            ),
            [FEE_STORE_LEVEL.SHIPPING_LABEL]: new PolicyFeeStore(
                shipping_label_fee,
                POLICY_FEES_DEFAULS_BY_FEE_STORE_LEVEL[
                    FEE_STORE_LEVEL.SHIPPING_LABEL
                ],
                this
            ),
        };
        this._conditionGroupBylevel = {
            [CONDITION_GROUP_LEVELS.ORDER]:
                model.policy_rule_condition_groups_order_level?.map(
                    (group) =>
                        new PolicyConditionGroupStore(
                            this,
                            group,
                            CONDITION_GROUP_LEVELS.ORDER
                        )
                ) ?? [],
            [CONDITION_GROUP_LEVELS.CUSTOMER]:
                model.policy_rule_condition_groups_customer_level?.map(
                    (group) =>
                        new PolicyConditionGroupStore(
                            this,
                            group,
                            CONDITION_GROUP_LEVELS.CUSTOMER
                        )
                ) ?? [],
            [CONDITION_GROUP_LEVELS.ITEM]:
                model.policy_rule_condition_groups?.map(
                    (group) =>
                        new PolicyConditionGroupStore(
                            this,
                            group,
                            CONDITION_GROUP_LEVELS.ITEM
                        )
                ) ?? [],
            [CONDITION_GROUP_LEVELS.RMA]:
                model.policy_rule_condition_groups_rma_level?.map(
                    (group) =>
                        new PolicyConditionGroupStore(
                            this,
                            group,
                            CONDITION_GROUP_LEVELS.RMA
                        )
                ) ?? [],
        };
    }

    public setChanges(changes: IPolicyRuleChanges) {
        delete changes.id;

        this._changes = changes;

        if (changes.policy_rule_availability) {
            this._changes.policy_rule_availability =
                changes.policy_rule_availability;
        }

        /** generate or set changes to all sub stores here */
        const restocking_fee_changes = changes.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.RESTOCKING_FEE
        );
        const cancellation_fee_changes = changes.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.CANCELLATION_FEE
        );
        const return_fee_changes = changes.policy_rule_fees?.find(
            (policy_fee) => policy_fee.fee_type === FEE_TYPE.RETURN_FEE
        );
        const shipping_label_fee_changes = changes.policy_rule_fees?.find(
            (policy_fee) => {
                return (
                    !policy_fee.fee_type === null ||
                    SHIPPING_LABEL_FEE_TYPES.indexOf(
                        policy_fee.fee_type as FEE_TYPE
                    ) >= 0
                );
            }
        );

        if (restocking_fee_changes) {
            this._fees[FEE_STORE_LEVEL.RESTOCK].setChanges(
                restocking_fee_changes
            );
        }
        if (cancellation_fee_changes) {
            this._fees[FEE_STORE_LEVEL.RETURN].setChanges(
                cancellation_fee_changes
            );
        }
        if (return_fee_changes) {
            this._fees[FEE_STORE_LEVEL.CANCEL].setChanges(return_fee_changes);
        }
        if (shipping_label_fee_changes) {
            this._fees[FEE_STORE_LEVEL.SHIPPING_LABEL].setChanges(
                shipping_label_fee_changes
            );
        }

        this._conditionGroupBylevel = {
            [CONDITION_GROUP_LEVELS.ORDER]:
                changes.policy_rule_condition_groups_order_level?.map(
                    (groupChanges) => {
                        const groupStore = new PolicyConditionGroupStore(
                            this,
                            {} as IPolicyConditionGroup,
                            CONDITION_GROUP_LEVELS.ORDER
                        );
                        groupStore.setChanges(groupChanges);
                        return groupStore;
                    }
                ) ?? [],
            [CONDITION_GROUP_LEVELS.CUSTOMER]:
                changes.policy_rule_condition_groups_customer_level?.map(
                    (groupChanges) => {
                        const groupStore = new PolicyConditionGroupStore(
                            this,
                            {} as IPolicyConditionGroup,
                            CONDITION_GROUP_LEVELS.CUSTOMER
                        );
                        groupStore.setChanges(groupChanges);
                        return groupStore;
                    }
                ) ?? [],
            [CONDITION_GROUP_LEVELS.ITEM]:
                changes.policy_rule_condition_groups?.map((groupChanges) => {
                    const groupStore = new PolicyConditionGroupStore(
                        this,
                        {} as IPolicyConditionGroup,
                        CONDITION_GROUP_LEVELS.ITEM
                    );
                    groupStore.setChanges(groupChanges);
                    return groupStore;
                }) ?? [],
            [CONDITION_GROUP_LEVELS.RMA]:
                changes.policy_rule_condition_groups_rma_level?.map(
                    (groupChanges) => {
                        const groupStore = new PolicyConditionGroupStore(
                            this,
                            {} as IPolicyConditionGroup,
                            CONDITION_GROUP_LEVELS.RMA
                        );
                        groupStore.setChanges(groupChanges);
                        return groupStore;
                    }
                ) ?? [],
        };
    }

    private get policyService(): PolicyService {
        return rootContainer.get<PolicyService>(TYPE.PolicyService);
    }
    private get _selectedResolutionIsReturnGo(): boolean {
        return !!this.resolution?.is_return_go;
    }

    public isIdEquals(id?: string | number): boolean {
        return (
            this._model.id === Number(id) ||
            this._changes._client_id === (id ?? "").toString()
        );
    }

    public isSaving(): boolean {
        return this._isSaving;
    }
    public isLoading(): boolean {
        return this.isSaving() || this._isDeleting;
    }
    public get name(): string {
        return this._changes.name ?? this._model.name;
    }
    public get id() {
        return this._changes._client_id ?? this._model.id;
    }
    public get isExisting(): boolean {
        return !!this._model.id;
    }
    public get returnMethodId(): number | undefined | null {
        if (this._changes.return_method_id === null) {
            return null;
        }
        return this._changes.return_method_id ?? this._model.return_method_id;
    }
    public get resolutionId(): number {
        return this._changes.resolution_id ?? this._model.resolution_id;
    }
    public get isEnabled(): boolean {
        return this._changes.is_enabled ?? this._model.is_enabled;
    }
    public get selectedResolutionId(): RESOLUTION_ID | undefined {
        return this.resolution?.resolution_id;
    }
    public get selectedResolutionType(): RESOLUTION_TYPE | undefined {
        return this.resolution?.resolution_type;
    }
    public get selectedResolutionRefundProvider():
        | INSTANT_REFUND_PROVIDER
        | string {
        return this.resolution?.instant_refund_provider ?? "";
    }
    public get selectedResolutionIcon(): string | null {
        if (!!this.resolution?.is_return_go) {
            return IconReturnGoScoreAlgo;
        }

        if (this.selectedResolutionRefundProvider !== "") {
            switch (this.selectedResolutionRefundProvider) {
                case INSTANT_REFUND_PROVIDER.Refundid:
                    return IconProviderRefundid;
                case INSTANT_REFUND_PROVIDER.Reveni:
                    return IconProviderReveni;
                case INSTANT_REFUND_PROVIDER.Rise:
                    return IconProviderRise;
            }
        }

        return null;
    }
    public get isAutoApproved(): APPROVAL_PROCESS {
        return this._changes.is_auto_approved ?? this._model.is_auto_approved;
    }
    public get isAutoExchange(): boolean {
        return this._changes.is_auto_exchange ?? this._model.is_auto_exchange;
    }
    public get autoExchangeTrigger(): AUTO_PROCESS_TRIGGER | null {
        if (this._changes.auto_exchange_trigger === null) {
            return null;
        }
        return (
            this._changes.auto_exchange_trigger ??
            this._model.auto_exchange_trigger
        );
    }
    public get autoExchangeValidatedQualifier():
        | AUTO_PROCESS_VALIDATED_QUALIFIER
        | null
        | undefined {
        if (this._changes.auto_exchange_validated_qualifier === null) {
            return null;
        }
        return (
            this._changes.auto_exchange_validated_qualifier ??
            this._model.auto_exchange_validated_qualifier
        );
    }
    public get autoExchangeExecute(): AUTO_PROCESS_EXECUTE | null {
        if (this._changes.auto_exchange_execute === null) {
            return null;
        }
        return (
            this._changes.auto_exchange_execute ??
            this._model.auto_exchange_execute
        );
    }
    public get autoExchangeSinceValue(): number | null | undefined {
        if (this._changes.auto_exchange_since_value === null) {
            return null;
        }
        return (
            this._changes.auto_exchange_since_value ??
            this._model.auto_exchange_since_value
        );
    }
    public get autoExchangeSinceUnit(): SINCE_UNIT | null | undefined {
        if (this._changes.auto_exchange_since_unit === null) {
            return null;
        }
        return (
            this._changes.auto_exchange_since_unit ??
            this._model.auto_exchange_since_unit
        );
    }
    public get capturePaymentTrigger(): AUTO_PROCESS_TRIGGER {
        return (
            this._changes.capture_payment_trigger ??
            this._model.capture_payment_trigger
        );
    }
    public get capturePaymentValidatedQualifier():
        | AUTO_PROCESS_VALIDATED_QUALIFIER
        | null
        | undefined {
        if (this._changes.capture_payment_validated_qualifier === null) {
            return null;
        }
        return (
            this._changes.capture_payment_validated_qualifier ??
            this._model.capture_payment_validated_qualifier
        );
    }
    public get isAutoRefund(): boolean {
        return this._changes.is_auto_refund ?? this._model.is_auto_refund;
    }
    public get autoRefundTrigger(): AUTO_PROCESS_TRIGGER | null {
        if (this._changes.auto_refund_trigger === null) {
            return null;
        }
        return (
            this._changes.auto_refund_trigger ?? this._model.auto_refund_trigger
        );
    }
    public get autoRefundValidatedQualifier():
        | AUTO_PROCESS_VALIDATED_QUALIFIER
        | null
        | undefined {
        if (this._changes.auto_refund_validated_qualifier === null) {
            return null;
        }
        return (
            this._changes.auto_refund_validated_qualifier ??
            this._model.auto_refund_validated_qualifier
        );
    }
    public get autoRefundExecute(): AUTO_PROCESS_EXECUTE | null {
        if (this._changes.auto_refund_execute === null) {
            return null;
        }
        return (
            this._changes.auto_refund_execute ?? this._model.auto_refund_execute
        );
    }
    public get autoRefundSinceValue(): number | null | undefined {
        if (this._changes.auto_refund_since_value === null) {
            return null;
        }
        return (
            this._changes.auto_refund_since_value ??
            this._model.auto_refund_since_value
        );
    }
    public get autoRefundSinceUnit(): SINCE_UNIT | null | undefined {
        if (this._changes.auto_refund_since_unit === null) {
            return null;
        }
        return (
            this._changes.auto_refund_since_unit ??
            this._model.auto_refund_since_unit
        );
    }
    public get selectedExecuteOption(): AUTO_PROCESS_EXECUTE {
        return this._changes.auto_mark_done_execute ??
            this._model.auto_mark_done_execute ??
            AUTO_PROCESS_EXECUTE.IMMEDIATELY;
    }
    public get executeSinceValue(): number | null {
        return (
            this._changes.auto_mark_done_since_value ??
            this._model.auto_mark_done_since_value
        );
    }
    public get executeSinceUnit(): SINCE_UNIT | null {
        return (
            this._changes.auto_mark_done_since_unit ??
            this._model.auto_mark_done_since_unit
        );
    }

    public getAutoMarkDoneTriggerOptionsTree(): ICheckboxesTreeItem[] {
        const isNewPolicyRule = !this._model.id
        const triggerString = isNewPolicyRule
            ? this._convertAutoMarkDoneTriggerTreeIntoString(AUTO_MARK_DONE_TRIGGER_OPTIONS_TREE)
            : (
                this._changes.auto_mark_done_trigger ??
                this._model.auto_mark_done_trigger
            );
        return this._convertAutoMarkDoneTriggerIntoOptionsTree(triggerString, AUTO_MARK_DONE_TRIGGER_OPTIONS_TREE);
    }

    public get getAutoMarkDoneTriggersOptions(): ICheckboxesTreeItem[] {
        return AUTO_MARK_DONE_TRIGGER_OPTIONS_TREE.filter((item) => {
            // Hide "Exchange Released" trigger for non-exchange Policy Rules
            return this._isExchangeTypeResolutionSelected() || item.value !== AUTO_MARK_DONE_TRIGGER.EXCHANGE_RELEASED
        });
    }

    public get includeShippingAmountInRefund(): boolean {
        return (
            this._changes.include_shipping_amount_in_refund ??
            this._model.include_shipping_amount_in_refund
        );
    }

    public get refundProductDuties(): boolean {
        return (
            this._changes.refund_product_duties ??
            this._model.refund_product_duties
        );
    }
    public get autoMarkDone(): AUTO_MARK_DONE {
        return this._changes.auto_mark_done ?? this._model.auto_mark_done;
    }
    public get displayFeeOption(): DISPLAY_FEE_OPTION {
        return (
            this._changes.display_fee_option ?? this._model.display_fee_option
        );
    }
    public get chargeFees(): CHARGE_FEES {
        return this._changes.charge_fees ?? this._model.charge_fees;
    }
    public get returnLabelFeePayment(): string {
        return (
            this._changes.return_label_fee_payment ??
            this._model.return_label_fee_payment
        );
    }
    public get applyOn(): APPLY_ON {
        return this._changes.apply_on ?? this._model.apply_on;
    }
    public get displayAsDisabled(): boolean {
        return (
            this._changes.display_as_disabled ?? this._model.display_as_disabled
        );
    }
    public get enableReserveItems(): boolean {
        return (
            this._changes.enable_reserve_items ??
            this._model.enable_reserve_items
        );
    }
    public get notifyHelpdeskEnabled(): boolean {
        return this._changes.notify_helpdesk ?? this._model.notify_helpdesk;
    }
    public get restockTime(): string | null {
        return this._changes.restock_time ?? this._model.restock_time;
    }
    public get itemsReservationTimeframe(): number | null {
        return (
            this._changes.item_reservation_timeframe ??
            this._model.item_reservation_timeframe
        );
    }
    public get requireAddress(): boolean {
        return this._changes.require_address ?? this._model.require_address;
    }
    public get prefillAddress(): boolean {
        return this._changes.prefill_address ?? this._model.prefill_address;
    }
    public get showRequireAddress(): boolean {
        return this.resolution?.resolution_id !== RESOLUTION_ID.CANCEL_ORDER;
    }
    public get hasRCRM(): boolean {
        const aggregatorCode =
            this.returnMethod?.return_label_setting?.shipping_connection
                ?.aggregator_code;
        if (!aggregatorCode) {
            return false;
        }
        return this._aggregatorInfoPreset.isCoverage(aggregatorCode);
    }
    public get hasSBR(): boolean {
        const aggregatorCode: SHIPPING_CONNECTION_CODE | undefined =
            this.returnMethod?.return_label_setting?.shipping_connection
                ?.aggregator_code;
        if (aggregatorCode === undefined) {
            return false;
        }
        return this._aggregatorInfoPreset.isShipByReturnGo(aggregatorCode);
    }

    public get instantExchangeEnabled(): boolean {
        return (
            this._changes.instant_exchange_enabled ??
            this._model.instant_exchange_enabled
        );
    }

    private get _limitEligibleItemsOrderLevel(): boolean {
        return (
            this._changes.limit_eligible_items_order_level ??
            this._model.limit_eligible_items_order_level
        );
    }
    private get _limitEligibleItemsCustomerLevel(): boolean {
        return (
            this._changes.limit_eligible_items_customer_level ??
            this._model.limit_eligible_items_customer_level
        );
    }
    private get _limitEligibleItemsRMALevel(): boolean {
        return (
            this._changes.limit_eligible_items_rma_level ??
            this._model.limit_eligible_items_rma_level
        );
    }
    private get _limitEligibleItems(): boolean {
        return (
            this._changes.limit_eligible_items ??
            this._model.limit_eligible_items
        );
    }
    public get isCoverage(): boolean {
        return this._changes.is_coverage ?? this._model.is_coverage;
    }
    public get includeEuRightOfWithdrawalEmail(): boolean {
        return this._changes.include_eu_right_of_withdrawal_email ?? this._model.include_eu_right_of_withdrawal_email;
    }
    public get issuePartialRefund(): boolean {
        return (
            this._changes.issue_partial_refund ??
            this._model.issue_partial_refund
        );
    }

    public get autoMarkDoneAfterPartialValidation(): boolean {
        return (
            this._changes.auto_mark_done_after_partial_validation ??
            this._model.auto_mark_done_after_partial_validation
        );
    }

    public getIsLimitEligibleItemsByLevel(
        level: CONDITION_GROUP_LEVELS
    ): boolean {
        switch (level) {
            case CONDITION_GROUP_LEVELS.ORDER:
                return this._limitEligibleItemsOrderLevel;
            case CONDITION_GROUP_LEVELS.CUSTOMER:
                return this._limitEligibleItemsCustomerLevel;
            case CONDITION_GROUP_LEVELS.RMA:
                return this._limitEligibleItemsRMALevel;
            case CONDITION_GROUP_LEVELS.ITEM:
                return this._limitEligibleItems;
            default:
                throw new Error("unsupported conditions level " + level);
        }
    }

    private _setLimitEligibleItemsByLevel(
        value: boolean,
        level: CONDITION_GROUP_LEVELS
    ) {
        switch (level) {
            case CONDITION_GROUP_LEVELS.ORDER:
                this._changes.limit_eligible_items_order_level = value;
                break;
            case CONDITION_GROUP_LEVELS.CUSTOMER:
                this._changes.limit_eligible_items_customer_level = value;
                break;
            case CONDITION_GROUP_LEVELS.RMA:
                this._changes.limit_eligible_items_rma_level = value;
                break;
            case CONDITION_GROUP_LEVELS.ITEM:
                this._changes.limit_eligible_items = value;
                break;
        }
        this._clearUnneededChanges();
    }

    public setLimitEligibleItemsByLevel(
        value: boolean,
        level: CONDITION_GROUP_LEVELS
    ) {
        this._setLimitEligibleItemsByLevel(value, level);

        if (value) {
            /** when enabling group - make sure group exists and add new condition in edit mode if needed */
            const conditionGroupStore = this._ensureConditionGroup(level);
            if (conditionGroupStore.isEmpty()) {
                conditionGroupStore.addCondition();
            }
        } else {
            /** when switching condition group OFF - remove ghost conditions and the group if it is a ghost */
            this._conditionGroupBylevel[level] = this._conditionGroupBylevel[
                level
            ].filter((group) => {
                group.discardUncommittedChanges();
                if (group.isEmpty()) {
                    group.setDeleted(true);
                }
                return !group.isGhost();
            });
        }

        this._clearUnneededChanges();
    }

    public finalizeConditionGroup(level: CONDITION_GROUP_LEVELS) {
        this._conditionGroupBylevel[level] = this._conditionGroupBylevel[
            level
        ].filter((group) => {
            if (group.isEmpty()) {
                group.setDeleted(true);
            }
            return !group.isGhost();
        });

        /** if condition group was deleted - switch condition OFF */
        if (
            !this._conditionGroupBylevel[level].length ||
            this._conditionGroupBylevel[level][0]?.isEmpty()
        ) {
            this._setLimitEligibleItemsByLevel(false, level);
        }
    }

    /** creates new group if not exists and adds */
    private _ensureConditionGroup(
        level: CONDITION_GROUP_LEVELS
    ): PolicyConditionGroupStore {
        /** if there is no group - create it */
        let conditionGroupStore = this._conditionGroupBylevel[level].length
            ? this._conditionGroupBylevel[level][0]
            : undefined;
        conditionGroupStore?.setDeleted(false);

        if (!conditionGroupStore) {
            conditionGroupStore = new PolicyConditionGroupStore(
                this,
                {} as IPolicyConditionGroup,
                level
            );
            conditionGroupStore.setChanges({
                is_eligible: true,
                conditions_operator: CONDITION_GROUP_OPERATOR.OP_AND,
            });
            this._conditionGroupBylevel[level].push(conditionGroupStore);
        }
        return conditionGroupStore;
    }

    public get conditionPreset(): ConditionsPreset {
        return this._conditionPreset;
    }
    public get returnMethodsPreset(): ReturnMethodsPreset {
        return this._returnMethodsPreset;
    }
    public get returnReasonsPreset(): ReturnReasonsPreset {
        return this._returnReasonsPreset;
    }
    public get resolutionPreset(): ResolutionsPreset {
        return this._resolutionsPreset;
    }
    public get aggregatorInfoPreset(): AggregatorsInfoStore {
        return this._aggregatorInfoPreset;
    }
    public get addressRequirementsStore(): PolicyAddressRequirementsStore {
        return this._addressRequirementsStore;
    }

    public getConditionGroupListByLevel(level: CONDITION_GROUP_LEVELS) {
        return this._conditionGroupBylevel[level].filter(
            (group) => !group.deleted
        );
    }

    public get resolution(): IResolutionPreset | undefined {
        return this.resolutionPreset.getPresetById(this.resolutionId);
    }

    public get returnMethod(): IReturnMethodPreset | undefined {
        return this.returnMethodsPreset.getPresetById(this.returnMethodId);
    }

    public get selectedReturnMethodIconUrl(): string | null {
        const shippingConnectionSetting =
            this.returnMethod?.return_label_setting ??
            this.returnMethod?.drop_off_setting;
        const aggregatorCode =
            shippingConnectionSetting?.shipping_connection?.aggregator_code;

        return (
            this._aggregatorInfoPreset?.getLogoIconUrl(aggregatorCode) ?? null
        );
    }

    public getDisplayFees(): string[] {
        if (this.chargeFees === CHARGE_FEES.CHARGE_FEE) {
            const feeDisplayStrings = Object.keys(this._fees)
                .map((feeLevel) => {
                    return this.getFee(
                        feeLevel as FEE_STORE_LEVEL
                    )?.getDisplayString();
                })
                .filter((s) => !!s) as string[];

            /** if no expected fees are charged - show "Free" under "Fees" column  */
            if (feeDisplayStrings.length === 0) {
                feeDisplayStrings.push("Free");
            }

            return feeDisplayStrings;
        }

        return [DISPLAY_FEE_OPTION_MAP[this.displayFeeOption]?.label ?? ""];
    }

    public isGroupEnabled(level: CONDITION_GROUP_LEVELS): boolean {
        // if no resolutions selected - show all cards
        if (!this.resolution) {
            return true;
        }

        const currentLevelLimitations =
            this._conditionPreset.getPresetByLevel(
                level
            ).limitations_by_resolution_type;
        let limitationsForCurrentResolution = null;
        if (
            Object.keys(currentLevelLimitations).includes(
                this.resolution.resolution_type.toString()
            )
        ) {
            limitationsForCurrentResolution =
                currentLevelLimitations[this.resolution.resolution_type];
        }

        return (
            !limitationsForCurrentResolution ||
            limitationsForCurrentResolution.is_enabled
        );
    }

    public get availability(): IPolicyRuleAvailability[] {
        return (
            this._changes.policy_rule_availability ??
            this._model.policy_rule_availability ??
            []
        );
    }

    public setAvailability(values: IOption[]) {
        const validValues =
            this._ensureValidAvailabilityOptionsCombination(values);

        this._changes.policy_rule_availability = validValues.map(
            (v) => ({ used_for: v } as IPolicyRuleAvailability)
        );

        this._clearUnneededChanges();
    }

    public get requestMultipleShipments(): boolean {
        return (
            this._changes.request_multiple_shipments ??
            this._model.request_multiple_shipments
        );
    }
    public setRequestMultipleShipments(value: boolean) {
        this._changes.request_multiple_shipments = value;
        this._clearUnneededChanges();
    }

    private _ensureValidAvailabilityOptionsCombination(
        values: IOption[]
    ): USED_FOR_OPTIONS[] {
        const validValues = values.map((v) => v.value as USED_FOR_OPTIONS);
        const hasPortalOption = validValues.some(
            (a) => a === USED_FOR_OPTIONS.USED_FOR_PORTAL
        );

        /** If input values do not include portal availability - return as is */
        if (!hasPortalOption) {
            return validValues;
        }

        /** list of Create RMA related availability options */
        const cRmaAvailabilityOptions = [
            USED_FOR_OPTIONS.USED_FOR_CRMA_ONLINE_USERS,
            USED_FOR_OPTIONS.USED_FOR_CRMA_PHYSICAL_STORE_USERS,
        ];

        /** Make sure that Create RMA related availability options are selected */
        cRmaAvailabilityOptions.forEach((option) => {
            if (!validValues.includes(option)) {
                validValues.push(option);
            }
        });

        return validValues;
    }

    public setName(value: string) {
        this._changes.name = value;
        this._clearUnneededChanges();
    }

    public setIsEnabled(value: boolean) {
        const hasEnabledPolicies = this._policyListStore.hasEnabledPolicies();
        this._changes.is_enabled = value;

        this._shouldRefresh =
            hasEnabledPolicies !== this._policyListStore.hasEnabledPolicies();
        this._clearUnneededChanges();
    }

    public setIsAutoApproved(value: APPROVAL_PROCESS) {
        this._changes.is_auto_approved = value;
        this._alignReturnMethod();
        this._clearUnneededChanges();
    }

    public setIsAutoExchange(value: boolean) {
        this._changes.is_auto_exchange = value;

        if (value === false) {
            this._changes.auto_exchange_trigger = null;
            this._changes.auto_exchange_execute = null;
            this._changes.auto_exchange_validated_qualifier = null;
            this._changes.auto_exchange_since_value = null;
            this._changes.auto_exchange_since_unit = null;
        }

        this._clearUnneededChanges();

        // restore to inital value
        if (this._changes.is_auto_exchange === undefined) {
            delete this._changes.auto_exchange_trigger;
            delete this._changes.auto_exchange_execute;
            delete this._changes.auto_exchange_validated_qualifier;
            delete this._changes.auto_exchange_since_value;
            delete this._changes.auto_exchange_since_unit;
        }
    }

    public setAutoExchangeValidatedQualifier(
        value: AUTO_PROCESS_VALIDATED_QUALIFIER
    ) {
        this._changes.auto_exchange_validated_qualifier = value;
        this._clearUnneededChanges();
    }

    public setAutoExchangeTrigger(value: AUTO_PROCESS_TRIGGER) {
        this._changes.auto_exchange_trigger = value;

        if (value !== AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED) {
            this._changes.auto_exchange_validated_qualifier = null;
        }

        this._clearUnneededChanges();

        if (this._changes.auto_exchange_trigger === undefined) {
            delete this._changes.auto_exchange_validated_qualifier;
        }
    }

    public setAutoExchangeExecute(value: AUTO_PROCESS_EXECUTE) {
        this._changes.auto_exchange_execute = value;

        if (value === AUTO_PROCESS_EXECUTE.IMMEDIATELY) {
            this._changes.auto_exchange_since_value = null;
            this._changes.auto_exchange_since_unit = null;
        }

        this._clearUnneededChanges();

        // restore to inital value is auto_exchange_execute was restored
        if (this._changes.auto_exchange_execute === undefined) {
            delete this._changes.auto_exchange_since_value;
            delete this._changes.auto_exchange_since_unit;
        }

        /** if there will be need to set default values for since value and since unit - set it here on changes */
    }

    public setAutoExchangeSinceValue(value: number) {
        this._changes.auto_exchange_since_value = value;
        this._clearUnneededChanges();
    }

    public setAutoExchangeSinceUnit(value: SINCE_UNIT) {
        this._changes.auto_exchange_since_unit = value;
        this._clearUnneededChanges();
    }

    public setIsAutoRefund(value: boolean) {
        this._changes.is_auto_refund = value;

        if (value === false) {
            this._changes.auto_refund_trigger = null;
            this._changes.auto_refund_execute = null;
            this._changes.auto_refund_validated_qualifier = null;
            this._changes.auto_refund_since_value = null;
            this._changes.auto_refund_since_unit = null;
        }

        this._clearUnneededChanges();

        // restore to inital value
        if (this._changes.is_auto_refund === undefined) {
            delete this._changes.auto_refund_trigger;
            delete this._changes.auto_refund_execute;
            delete this._changes.auto_refund_validated_qualifier;
            delete this._changes.auto_refund_since_value;
            delete this._changes.auto_refund_since_unit;
        }
    }

    public setCapturePaymentTrigger(value: AUTO_PROCESS_TRIGGER) {
        this._changes.capture_payment_trigger = value;

        if (value !== AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED) {
            this._changes.capture_payment_validated_qualifier = null;
        }

        this._clearUnneededChanges();

        if (this._changes.capture_payment_trigger === undefined) {
            delete this._changes.capture_payment_validated_qualifier;
        }
    }

    public setCapturePaymentValidatedQualifier(
        value: AUTO_PROCESS_VALIDATED_QUALIFIER
    ) {
        this._changes.capture_payment_validated_qualifier = value;
        this._clearUnneededChanges();
    }

    public setAutoRefundTrigger(value: AUTO_PROCESS_TRIGGER) {
        this._changes.auto_refund_trigger = value;

        if (value !== AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED) {
            this._changes.auto_refund_validated_qualifier = null;
        }

        this._clearUnneededChanges();

        if (this._changes.auto_refund_trigger === undefined) {
            delete this._changes.auto_refund_validated_qualifier;
        }
    }

    public setAutoRefundValidatedQualifier(
        value: AUTO_PROCESS_VALIDATED_QUALIFIER
    ) {
        this._changes.auto_refund_validated_qualifier = value;
        this._clearUnneededChanges();
    }

    public setAutoRefundExecute(value: AUTO_PROCESS_EXECUTE) {
        this._changes.auto_refund_execute = value;

        if (value === AUTO_PROCESS_EXECUTE.IMMEDIATELY) {
            this._changes.auto_refund_since_value = null;
            this._changes.auto_refund_since_unit = null;
        }

        this._clearUnneededChanges();

        // restore to inital value is auto_refund_execute was restored
        if (this._changes.auto_refund_execute === undefined) {
            delete this._changes.auto_refund_since_value;
            delete this._changes.auto_refund_since_unit;
        }

        /** if there will be need to set default values for since value and since unit - set it here on changes */
    }

    public setAutoRefundSinceValue(value: number) {
        this._changes.auto_refund_since_value = value;
        this._clearUnneededChanges();
    }

    public setAutoRefundSinceUnit(value: SINCE_UNIT) {
        this._changes.auto_refund_since_unit = value;
        this._clearUnneededChanges();
    }

    public setSelectedExecuteOption(value: AUTO_PROCESS_EXECUTE) {
        this._changes.auto_mark_done_execute = value;
        this._clearUnneededChanges();
    }

    public setExecuteSinceValue(value: number) {
        this._changes.auto_mark_done_since_value = value;
        this._clearUnneededChanges();
    }

    public setExecuteSinceUnit(value: SINCE_UNIT) {
        this._changes.auto_mark_done_since_unit = value;
        this._clearUnneededChanges();
    }

    public setAutoMarkDoneTrigger(tree: ICheckboxesTreeItem[]) {
        this._changes.auto_mark_done_trigger = this._convertAutoMarkDoneTriggerTreeIntoString(tree);
        this._clearUnneededChanges();
    }

    private _convertAutoMarkDoneTriggerIntoOptionsTree(triggerString: string | null, availableOptions: ICheckboxesTreeItem[]): ICheckboxesTreeItem[] {
        if (!triggerString) {
            return [];
        }

        try {
            /*
             * Parse the string format "[foo, bar]" into an array of strings
             */
            const values = triggerString.split(',') as string[];
            if (!Array.isArray(values)) {
                return [];
            }

            /*
             * Helper function to find matching items recursively
             */
            const findMatchingItems = (options: ICheckboxesTreeItem[], targetValues: string[]): ICheckboxesTreeItem[] => {
                const result: ICheckboxesTreeItem[] = [];
                for (const option of options) {
                    if (targetValues.includes(option.value)) {
                        const matchedItem: ICheckboxesTreeItem = {
                            value: option.value,
                            label: option.label,
                            checkBoxValue: option.checkBoxValue,
                            disabled: option.disabled,
                        };
                        if (option.childNodes && option.childNodes.length > 0) {
                            matchedItem.childNodes = findMatchingItems(option.childNodes, targetValues);
                        }
                        result.push(matchedItem);
                    } else if (option.childNodes && option.childNodes.length > 0) {
                        /*
                         * Check children even if parent doesn't match
                         */
                        const childMatches = findMatchingItems(option.childNodes, targetValues);
                        if (childMatches.length > 0) {
                            result.push({
                                ...option,
                                childNodes: childMatches,
                            });
                        }
                    }
                }
                return result;
            };

            return findMatchingItems(availableOptions, values);
        } catch (e) {
            console.error(`Failed to parse auto_mark_done_trigger: ${e}`);
            return [];
        }
    }

    private _convertAutoMarkDoneTriggerTreeIntoString(tree: ICheckboxesTreeItem[]): string | null {
        if (!tree || tree.length === 0) {
            return '';
        }

        /*
         * Extract all values from the tree (including nested children)
         */
        const extractValues = (items: ICheckboxesTreeItem[]): string[] => {
            const values: string[] = [];
            for (const item of items) {
                values.push(item.value);
                if (item.childNodes && item.childNodes.length > 0) {
                    values.push(...extractValues(item.childNodes));
                }
            }
            return values;
        };

        const values = extractValues(tree);
        return values.join(',');
    }

    public setIncludeShippingAmountInRefund(value: boolean) {
        this._changes.include_shipping_amount_in_refund = value;
        this._clearUnneededChanges();
    }

    public setRefundProductDuties(value: boolean) {
        this._changes.refund_product_duties = value;
        this._clearUnneededChanges();
    }

    public setAutoMarkDone(value: AUTO_MARK_DONE) {
        this._changes.auto_mark_done = value;
        if (value === AUTO_MARK_DONE.MANUALLY) {
            this._changes.auto_mark_done_after_partial_validation = false;
        }
        this._clearUnneededChanges();
    }

    public setChargeFees(value: CHARGE_FEES) {
        this._adjustDisplayFeeOption(value);
        this._changes.charge_fees = value;
        /**
         * When we enable additional fees - need to set defaults for fees as changes if we did not receive fees from api,
         * when we switch OFF additional payments - need to clear changes
         */
        if (value === CHARGE_FEES.CHARGE_FEE) {
            Object.values(this._fees).forEach((fee) =>
                fee.applyDefaultsIfNeeded()
            );
        } else {
            /** restoring to model */
            Object.values(this._fees).forEach((fee) => fee.discardChanges());
        }

        this._alignFees();

        this._clearUnneededChanges();
    }

    public setDisplayFeeOption(value: DISPLAY_FEE_OPTION) {
        this._changes.display_fee_option = value;
        this._clearUnneededChanges();
    }

    public setInstantExchangeEnabled(v: boolean) {
        this._changes.instant_exchange_enabled = v;
        this._clearUnneededChanges();
    }

    public attemptToChangeReturnMethodId(v: number) {
        const sideEffectDetector = new SideEffectDetector();
        const sideEffect = sideEffectDetector.detect<PolicyStore>(
            () =>
                new PolicyStore(this._policyListStore, this.getUpdatedModel()),
            (store) => store._setReturnMethodId(v),
            ["return_method_id"]
        );

        if (sideEffect.hasSideEffects) {
            const optionsPreset =
                this.returnMethodsPreset.getOptionsPreset(this);
            this._sideEffect = {
                settingName: "Return Method",
                settingValue: optionsPreset.find((option) => option.value === v)
                    ?.label,
                confirmSideEffect: () => {
                    this._setReturnMethodId(v);
                    this._sideEffect = {};
                },
            };
            return;
        }
        this._setReturnMethodId(v);
    }

    private _setReturnMethodId(value: number | null) {
        this._changes.return_method_id = value;

        this._alignChargeFees();
        this._alignFees();

        if (this.hasRCRM) {
            this._changes.is_coverage = true;
        }

        this._clearUnneededChanges();
    }

    public get sideEffect(): ISideEffect {
        return this._sideEffect;
    }

    public cancelSideEffect() {
        this._sideEffect = {};
    }

    public attemptToChangeResolutionId(v: number) {
        const sideEffectDetector = new SideEffectDetector();
        const sideEffect = sideEffectDetector.detect<PolicyStore>(
            () =>
                new PolicyStore(this._policyListStore, this.getUpdatedModel()),
            (store) => store._setResolutionId(v),
            ["resolution_id"]
        );

        if (sideEffect.hasSideEffects) {
            this._sideEffect = {
                settingName: "Resolution",
                settingValue:
                    this.resolutionPreset.getPresetById(v)?.internal_name,
                confirmSideEffect: () => {
                    this._setResolutionId(v);
                    this._sideEffect = {};
                },
            };
            return;
        }
        this._setResolutionId(v);
    }

    private _setResolutionId(value: number) {
        this._changes.resolution_id = value;

        /** if instant refund resolution was selected, we should set approval process to auto */
        if (
            this.selectedResolutionType === RESOLUTION_TYPE.InstantRefundType ||
            this._selectedResolutionIsReturnGo
        ) {
            this.setIsAutoApproved(APPROVAL_PROCESS.AUTO);
        }

        this._alignApplyOn();
        this._alignChargeFees();
        this._alignFees();
        this._alignConditionGroups();
        this._alignReturnMethod();

        this._clearUnneededChanges();

        /** Alex: commented out restore - something is not right about it */
        // restore to inital value is auto_refund_execute was restored
        // if (this._changes.resolution_id === undefined) {
        //     delete this._changes.apply_on;
        // }
    }

    private _alignApplyOn() {
        if (
            !RESOLUTION_TYPES_SUPPORTING_GIFT_RETURNS_ELIGIBILITY.includes(
                this.selectedResolutionType as number
            )
        ) {
            if (this.applyOn !== this._getDefaultApplyOn()) {
                this._changes.apply_on = this._getDefaultApplyOn();
            }
        }
    }

    private _alignReturnMethod() {
        if (!this.returnMethodId) {
            return;
        }
        const returnMethodsOptions =
            this.returnMethodsPreset.getOptionsPreset(this);
        const currentReturnMethodOption = returnMethodsOptions.find(
            (o) => o.value === this.returnMethodId
        );

        if (!currentReturnMethodOption || currentReturnMethodOption.disabled) {
            this._setReturnMethodId(null);
        }
    }

    private _alignChargeFees() {
        if (this.isFeesDisabled) {
            if (this.chargeFees === CHARGE_FEES.CHARGE_FEE) {
                this.setChargeFees(CHARGE_FEES.DO_NOT_CHARGE_FEE);
            }
        } else {
            /** for coverage policy rules with RCRMS, we should charge fees and set the label fee to cost */
            if (this.hasRCRM) {
                this.setChargeFees(CHARGE_FEES.CHARGE_FEE);
                this.getFee(FEE_STORE_LEVEL.SHIPPING_LABEL)?.setFeeType(
                    FEE_TYPE.COST
                );
            }
        }
    }

    public get showDisplayAsDisabledConfirm() {
        return this._showDisplayAsDisabledConfirm;
    }

    public setDisplayAsDisabled(value: boolean) {
        /** if we already have policy with display as disabled and we enable other policy - we shold show confirm */
        if (value && this._policyListStore.displayAsDisabledRefundPolicy) {
            this._showDisplayAsDisabledConfirm = true;
            return;
        }

        this._changes.display_as_disabled = value;
        this._clearUnneededChanges();
    }

    public confirmSetDisplayAsDisabled() {
        this._showDisplayAsDisabledConfirm = false;
        this._changes.display_as_disabled = true;
    }

    public rejectSetDisplayAsDisabled() {
        this._showDisplayAsDisabledConfirm = false;
    }

    /** this fiels is invisible when not OFF - we can anly enable it manually by customer support request */
    public setRequireAddress(value: boolean) {
        this._changes.require_address = value;
        if (!value) {
            this.setPrefillAddress(false);
        } else {
            this.setPrefillAddress(true);
        }
        this._clearUnneededChanges();
    }

    public setPrefillAddress(value: boolean) {
        this._changes.prefill_address = value;
    }

    public isRefundableResolutionSelected(): boolean {
        if (!this.resolution) {
            return false;
        }

        return (
            REFUND_RESOLUTIONS_TYPES.includes(
                this.resolution.resolution_type
            ) ||
            (this._isExchangeTypeResolutionSelected() &&
                this.resolution.indicate_less_expensive)
        );
    }

    private _isExchangeTypeResolutionSelected(): boolean {
        if (!this.resolution) {
            return false;
        }

        return [
            RESOLUTION_TYPE.DirectVariantExchange,
            RESOLUTION_TYPE.ManualExchangeType,
            RESOLUTION_TYPE.ProductExchangeType,
        ].includes(this.resolution.resolution_type);
    }

    public isCancelResolutionSelected(): boolean {
        return (
            this.resolution?.resolution_type === RESOLUTION_TYPE.CancelOrderType
        );
    }

    public setReturnLabelFeePayment(value: RETURN_LABEL_FEE_PAYMENT) {
        this._changes.return_label_fee_payment = value;
        this._clearUnneededChanges();
    }

    public setApplyOn(value: APPLY_ON) {
        this._changes.apply_on = value;
        this._clearUnneededChanges();
    }

    public setNotifyHelpdesk(value: boolean) {
        this._changes.notify_helpdesk = value;
        this._clearUnneededChanges();
    }

    public setEnableReserveItems(value: boolean) {
        if (this.isReserveItemsDisabled) {
            throw new Error("setEnableReserveItems is disabled");
        }
        this._changes.enable_reserve_items = value;
        this._clearUnneededChanges();

        if (!value) {
            /** discarding changes when switching OFF */
            delete this._changes["item_reservation_timeframe"];
            delete this._changes["restock_time"];
        }
    }

    public setItemReservationTimeframe(v: number) {
        if (this.isReserveItemsDisabled) {
            throw new Error("setItemReservationTimeframe is disabled");
        }
        this._changes.item_reservation_timeframe = v;
        this._clearUnneededChanges();
    }

    public setRestockTime(v: string) {
        if (this.isReserveItemsDisabled) {
            throw new Error("setRestockTime is disabled");
        }
        this._changes.restock_time = v;
        this._clearUnneededChanges();
    }

    public setIssuePartialRefund(value: boolean) {
        this._changes.issue_partial_refund = value;
        this._clearUnneededChanges();
    }

    public setAutoMarkDoneAfterPartialValidation(value: boolean) {
        this._changes.auto_mark_done_after_partial_validation = value;
        this._clearUnneededChanges();
    }

    public get isFeesDisabled(): boolean {
        const returnMethod = this._returnMethodsPreset.getPresetById(
            this.returnMethodId
        );
        return (
            returnMethod?.type === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED &&
            this.resolution?.resolution_id !== RESOLUTION_ID.CANCEL_ORDER
        );
    }

    public get isShipByAnyCarrier(): boolean {
        const returnMethod = this._returnMethodsPreset.getPresetById(
            this.returnMethodId
        );
        return returnMethod?.type === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach((key) => {
            const theKey = key as keyof IPolicyRule;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });

        /** cleanup availability changes */
        if (
            this._model.policy_rule_availability?.length ===
                this._changes.policy_rule_availability?.length &&
            this._model.policy_rule_availability?.every((a) =>
                this._changes.policy_rule_availability?.find(
                    (cA) => cA.used_for === a.used_for
                )
            )
        ) {
            delete this._changes.policy_rule_availability;
        }
    }

    public get isLabelCostSupported() {
        const returnMethod = this._returnMethodsPreset.getPresetById(
            this.returnMethodId
        );
        return this._aggregatorInfoPreset.getIsLabelCostSupported(
            returnMethod?.return_label_setting?.shipping_connection
                ?.aggregator_code
        );
    }

    private _isTrackingSupported() {
        const returnMethod = this._returnMethodsPreset.getPresetById(
            this.returnMethodId
        );
        let aggregatorCode;
        if (returnMethod?.type === RETURN_METHOD_TYPE.DROP_OFF_LOCATION) {
            aggregatorCode =
                returnMethod.drop_off_setting?.shipping_connection
                    .aggregator_code;
        } else if (
            returnMethod?.type === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL
        ) {
            aggregatorCode =
                returnMethod.return_label_setting?.shipping_connection
                    .aggregator_code;
        }
        return [
            TRACKING_TYPE.TRACKING_TYPE_AUTOMATIC,
            TRACKING_TYPE.TRACKING_TYPE_MANUAL_REGISTRATION,
        ].includes(
            this._aggregatorInfoPreset.getTrackingType(
                aggregatorCode
            ) as TRACKING_TYPE
        );
    }

    private _getDefaultApplyOn() {
        return APPLY_ON.ALL_EXCEPT_GIFTS;
    }

    public get isMultipleShipmentsSupported(): boolean {
        /** if support multiple shipments is not available - do not allow multiple shipments */
        if (
            !currentPortalStore.isFeatureAllowed(
                FEATURES.BETA_SUPPORT_MULTIPLE_SHIPMENTS_FEATURE
            )
        ) {
            return false;
        }

        /** if no return method selected - do not allow multiple shipments */
        if (!this.returnMethod) {
            return false;
        }

        const allowedRmTypes = [
            RETURN_METHOD_TYPE.DROP_OFF_LOCATION,
            RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL,
        ];
        /** if return method type not supported - do not allow multiple shipments */
        if (!allowedRmTypes.includes(this.returnMethod.type)) {
            return false;
        }

        const connectionSettings =
            this.returnMethod.drop_off_setting ??
            this.returnMethod.return_label_setting;
        /** if return method does not have connection settings - do not allow multiple shipments */
        if (!connectionSettings) {
            return false;
        }

        return this._aggregatorInfoPreset.isMultipleShipmentsPerRmaSupported(
            connectionSettings.shipping_connection.aggregator_code
        );
    }

    public get isDisplayAsDisableSupported() {
        return (
            this.resolution?.resolution_type === RESOLUTION_TYPE.Refund &&
            currentPortalStore.setting?.showDisabledResolution
        );
    }

    public getFee(level: FEE_STORE_LEVEL) {
        /** TODO: consider RMs */

        /** when switch for charge fees is OFF - do not display fees (and delete them) */
        if (this.chargeFees !== CHARGE_FEES.CHARGE_FEE) {
            return undefined;
        }

        /** when fees are disabled - we should delete them */
        if (this.isFeesDisabled) {
            return undefined;
        }

        if (
            !this.resolution ||
            (level === FEE_STORE_LEVEL.CANCEL &&
                this.resolution.resolution_type !==
                    RESOLUTION_TYPE.CancelOrderType) ||
            (level !== FEE_STORE_LEVEL.CANCEL &&
                this.resolution.resolution_type ===
                    RESOLUTION_TYPE.CancelOrderType)
        ) {
            return undefined;
        }

        if (
            level === FEE_STORE_LEVEL.SHIPPING_LABEL &&
            this.returnMethod?.type !==
                RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER &&
            this.returnMethod?.type !==
                RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL
        ) {
            return undefined;
        }

        return this._fees[level];
    }

    public get isReserveItemsSupported(): boolean {
        return (
            !!this.resolution?.resolution_type &&
            [
                RESOLUTION_TYPE.DirectVariantExchange,
                RESOLUTION_TYPE.ManualExchangeType,
                RESOLUTION_TYPE.ProductExchangeType,
            ].indexOf(this.resolution.resolution_type) >= 0
        );
    }

    public get isReserveItemsDisabled() {
        return !currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS);
    }

    // Creating restock time options with an interval of 1 hr formatted like hh:mm: ex: 01:00, 02:00 ...
    public get restockTimeOptions() {
        const restockTimeOptions: IOption[] = [];
        for (let i = 0; i < 24; i++) {
            restockTimeOptions.push({
                label: i.toString().padStart(2, "0") + ":00",
                value: i.toString().padStart(2, "0") + ":00",
            });
        }
        return restockTimeOptions;
    }

    public get approvalProcessOptions() {
        const isDisabled = (v: APPROVAL_PROCESS) => {
            return (
                this._selectedResolutionIsReturnGo &&
                v === APPROVAL_PROCESS.MANUAL
            );
        };
        return approvalProcessOptionsList().map((option) => ({
            value: option.value,
            label: option.label,
            disabled: isDisabled(option.value),
        }));
    }

    public get instantRefundTriggerOptionsList(): IOption[] {
        const triggerName = currentPortalStore.setting?.capturePaymentTrigger;
        return [
            {
                value: AUTO_PROCESS_TRIGGER.USE_DEFAULT_TRIGGER,
                label: `Default from store settings (${triggerName})`,
            },
            {
                value: AUTO_PROCESS_TRIGGER.SHIPMENT_SHIPPED,
                label: "Shipment is in-transit",
            },
            {
                value: AUTO_PROCESS_TRIGGER.SHIPMENT_RECEIVED,
                label: "Shipment has been received",
            },
            {
                value: AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED,
                label: "Items have been validated",
            },
        ];
    }

    public getUsedForOptions(): IOption[] {
        const portalName = currentPortalStore.currentPortal.getPortalName();

        const cRmaDisabled = this.availability?.some(
            (a) => a.used_for === USED_FOR_OPTIONS.USED_FOR_PORTAL
        );

        return [
            { value: USED_FOR_OPTIONS.USED_FOR_PORTAL, label: portalName },
            {
                value: USED_FOR_OPTIONS.USED_FOR_CRMA_ONLINE_USERS,
                label: "Create RMA - Online users",
                disabled: cRmaDisabled,
            },
            {
                value: USED_FOR_OPTIONS.USED_FOR_CRMA_PHYSICAL_STORE_USERS,
                label: "Create RMA - Physical store users",
                disabled: cRmaDisabled,
            },
            {
                value: USED_FOR_OPTIONS.USED_FOR_CRMA_VIA_RETURNGO_API,
                label: "Create RMA - API",
            },
        ];
    }

    public get isResolutionAllowed(): boolean {
        const featureId: FEATURES | null = this.selectedResolutionId
            ? FEATURES_RESOLUTIONS_IDS_MAP[this.selectedResolutionId]
            : null;
        if (featureId) {
            return currentPortalStore.isFeatureAllowed(featureId);
        }

        return true;
    }

    public get showProductExchangeInfo(): boolean {
        return (
            this.resolution?.resolution_type ===
                RESOLUTION_TYPE.ProductExchangeType &&
            (!!this.resolution?.indicate_less_expensive ||
                !!this.resolution?.present_more_expensive)
        );
    }

    public duplicate(): number | string {
        return this._policyListStore.addPolicy(this);
    }

    /**
     * Determines if the refund product duties toggle should be displayed.
     * Shows when resolution is set, type is Refund, and Global-e direct refund integration is enabled.
     */
    public shouldShowRefundProductDutiesToggle(): boolean {
        return !!(
            this.resolution &&
            this.selectedResolutionType === RESOLUTION_TYPE.Refund &&
            this._policyListStore.hasGlobalEDirectRefundIntegration
        );
    }

    public get shouldShowInstantExchangeSettings(): boolean {
        const instantExchangeSetting =
            currentPortalStore.setting?.instantExchangeStore;
        const hasPaymentServiceIntegration =
            instantExchangeSetting?.hasPaymentServiceIntegration;
        const isInstantExchangeAllowed =
            currentPortalStore.isFeatureAllowed(FEATURES.INSTANT_EXCHANGE) &&
            hasPaymentServiceIntegration;

        /** Ensure that Instant Exchange Enabled */
        if (!instantExchangeSetting?.isActive || !isInstantExchangeAllowed) {
            return false;
        }
        /** Ensure the resolution is exchange / product exchange */
        if (
            !RELEASE_ABLE_RESOLUTIONS_IDS.includes(
                this.resolution?.resolution_id as RESOLUTION_ID
            )
        ) {
            return false;
        }

        if (!this.isAutoApproved) {
            return false;
        }

        /** Available only for RM that is ship with pre paid label OR drop-off, that supports tracking */
        return (
            [
                RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL,
                RETURN_METHOD_TYPE.DROP_OFF_LOCATION,
            ].includes(this.returnMethod?.type as RETURN_METHOD_TYPE) &&
            this._isTrackingSupported()
        );
    }

    public get _hasAssignedProductCases() {
        return this._model._has_assigned_product_cases;
    }

    private _alignFees(): void {
        Object.keys(FEE_STORE_LEVEL).forEach((feeLevel) => {
            const shouldDelete = !this.getFee(feeLevel as FEE_STORE_LEVEL);
            this._fees[feeLevel].setDeleted(shouldDelete);
            if (!shouldDelete) {
                this._fees[feeLevel].alignFeeType();
            }
        });
    }

    private _adjustDisplayFeeOption(value: CHARGE_FEES): void {
        // Waive all fees has it's special display fee option, so after changing from Waive all fees to Do not charge fee - we need to reset display option
        if (
            value === CHARGE_FEES.DO_NOT_CHARGE_FEE &&
            this.chargeFees === CHARGE_FEES.WAIVE_FEES
        ) {
            this.setDisplayFeeOption(DISPLAY_FEE_OPTION.FREE);
        }
    }

    private _alignConditionGroups() {
        Object.values(this._conditionGroupBylevel).forEach((groupList) =>
            groupList.forEach((group) => group.alignConditions())
        );
    }

    hasChanges(): boolean {
        const hasConditionGroupChanges =
            Object.values(this._conditionGroupBylevel).some((groupList) =>
                groupList.some((group) => group.hasChanges())
            ) ?? false;
        const hasFeeChanges =
            Object.values(this._fees).some((fee) => fee?.hasChanges()) ?? false;
        const hasAddressRequirementsChanges =
            this._addressRequirementsStore.hasChanges();

        return (
            !!Object.keys(this._changes).length ||
            hasConditionGroupChanges ||
            hasFeeChanges ||
            hasAddressRequirementsChanges
        );
    }

    discardChanges(): void {
        if (!this._model.id) {
            this._policyListStore.discardPolicy(this);
        } else {
            this._changes = {};
            Object.values(this._conditionGroupBylevel).forEach((groupList) =>
                groupList.forEach((group) => group.discardChanges())
            );
            Object.values(this._fees).forEach((fee) => fee?.discardChanges());
            this._addressRequirementsStore.discardChanges();
        }

        this._shouldRefresh = false;
    }

    getUpdatedModel(): IPolicyRule {
        const hasThisModelChanges =
            Object.keys(this._changes).length > 0 ||
            this._addressRequirementsStore.hasChanges();

        const policy_rule_condition_groups = this._conditionGroupBylevel[
            CONDITION_GROUP_LEVELS.ITEM
        ].map((g) => g.getUpdatedModel());
        const policy_rule_condition_groups_customer_level =
            this._conditionGroupBylevel[CONDITION_GROUP_LEVELS.CUSTOMER].map(
                (g) => g.getUpdatedModel()
            );
        const policy_rule_condition_groups_order_level =
            this._conditionGroupBylevel[CONDITION_GROUP_LEVELS.ORDER].map((g) =>
                g.getUpdatedModel()
            );
        const policy_rule_condition_groups_rma_level =
            this._conditionGroupBylevel[CONDITION_GROUP_LEVELS.RMA].map((g) =>
                g.getUpdatedModel()
            );

        const updatedModel = {
            ...this._model,
            ...this._changes,
            _has_changes: hasThisModelChanges,
        };

        /** Cancel order is a pre-shipment cancellation -- no return label or address collection is involved. Enforce at model level regardless of editor state. */
        if (this.selectedResolutionId === RESOLUTION_ID.CANCEL_ORDER) {
            updatedModel.require_address = false;
            updatedModel.prefill_address = false;
        }

        updatedModel.policy_rule_condition_groups =
            policy_rule_condition_groups as IPolicyConditionGroup[];
        updatedModel.policy_rule_condition_groups_customer_level =
            policy_rule_condition_groups_customer_level as IPolicyConditionGroup[];
        updatedModel.policy_rule_condition_groups_order_level =
            policy_rule_condition_groups_order_level as IPolicyConditionGroup[];
        updatedModel.policy_rule_condition_groups_rma_level =
            policy_rule_condition_groups_rma_level as IPolicyConditionGroup[];

        /** some fees that were created on client and also were deleted will return undefined as updated model - filter those out */
        updatedModel.policy_rule_fees = Object.values(this._fees)
            .map((fee) => fee?.getUpdatedModel())
            .filter((m) => !!m) as IPolicyFee[];

        // Include address requirements from the store
        updatedModel.address_requirements =
            this._addressRequirementsStore.getUpdatedModel();

        return updatedModel as IPolicyRule;
    }

    public isEditingConditions(): boolean {
        return Object.values(this._conditionGroupBylevel).some((groupList) =>
            groupList.some((group) => group.isEditMode)
        );
    }

    public async save(): Promise<void> {
        const updatedModel: WithOld<IPolicyRule> = this.getUpdatedModel();
        const wasPersistedBeforeSave = !!updatedModel.id;

        this._isSaving = true;

        let savedPolicyRule: IPolicyRule | undefined = undefined;
        let shouldRefreshList = false;

        try {
            if (updatedModel.id) {
                updatedModel.__old = this._model;

                const policyUpdateResponse =
                    await this.policyService.updatePolicyRule(
                        updatedModel.id,
                        updatedModel
                    );
                savedPolicyRule = policyUpdateResponse.policy;
                shouldRefreshList = policyUpdateResponse.should_refresh_list;
            } else {
                const policyCreateResponse =
                    await this.policyService.createPolicyRule(updatedModel);
                savedPolicyRule = policyCreateResponse.policy;
                shouldRefreshList = policyCreateResponse.should_refresh_list;
            }

            if (savedPolicyRule) {
	            if (!wasPersistedBeforeSave) {
		            rmsMixpanelStore.trackEvent(
			            POLICY_RULES_MIXPANEL_SAVE_NEW_RULE
		            );
	            }
	            const category =
		            this._policyListStore.getCategoryForPolicy(this) ??
		            this._policyListStore.getCategoryForResolutionId(
			            savedPolicyRule.resolution_id
		            );
	            if (category !== undefined) {
		            rmsMixpanelStore.trackEvent(
			            getPolicyRulesMixpanelSaveEvent(category)
		            );
	            }
            }

            if (shouldRefreshList) {
                await this._policyListStore.load();
            }
        } catch (e) {
            console.error(`PolicyStore.save: ${e}`);
        }

        if (this._shouldRefresh) {
            this._iframeService.refreshParentFrame();
        }

        runInAction(() => {
            if (savedPolicyRule) {
                this._setModel(savedPolicyRule);
            }
            this._shouldRefresh = false;
            this._isSaving = false;
        });
        return Promise.resolve();
    }

    public async delete(): Promise<void> {
        this._isDeleting = true;
        try {
            await this.policyService.deletePolicyRule(this._model.id);
            await this._policyListStore.load();
        } catch (e) {
            console.log(`Faild to delete policy rule due to error: ${e}`);
        }

        runInAction(() => {
            this._isDeleting = false;
        });
    }

    public setIsCoverage(v: boolean) {
        this._changes.is_coverage = v;
        this._clearUnneededChanges();
    }

    public setIncludeEuRightOfWithdrawalEmail(v: boolean) {
        this._changes.include_eu_right_of_withdrawal_email = v;
        this._clearUnneededChanges();
    }
}

export default PolicyStore;

/** some fields receievd from an api has an incorrect type */
function ensureValidModelCopy(data: IPolicyRule): IPolicyRule {
    return {
        ...data,
        is_enabled: toBoolean(data.is_enabled),
        is_auto_exchange: toBoolean(data.is_auto_exchange),
        is_auto_refund: toBoolean(data.is_auto_refund),
        limit_eligible_items_order_level: toBoolean(
            data.limit_eligible_items_order_level
        ),
        limit_eligible_items_customer_level: toBoolean(
            data.limit_eligible_items_customer_level
        ),
        limit_eligible_items_rma_level: toBoolean(
            data.limit_eligible_items_rma_level
        ),
        limit_eligible_items: toBoolean(data.limit_eligible_items),
        display_as_disabled: toBoolean(data.display_as_disabled),
        enable_reserve_items: toBoolean(data.enable_reserve_items),
        notify_helpdesk: toBoolean(data.notify_helpdesk),
        restock_time: data.restock_time?.toString() ?? null,
        item_reservation_timeframe: toNullableNumber(
            data.item_reservation_timeframe
        ),
        require_address: toBoolean(data.require_address),
        include_shipping_amount_in_refund: toBoolean(
            data.include_shipping_amount_in_refund
        ),
        instant_exchange_enabled: toBoolean(data.instant_exchange_enabled),
        request_multiple_shipments: toBoolean(data.request_multiple_shipments),
        is_coverage: toBoolean(data.is_coverage),
        include_eu_right_of_withdrawal_email: toBoolean(data.include_eu_right_of_withdrawal_email),
    };
}
